// Phone field helpers shared by the Formik PhoneInput, the form schemas and the Netlify form
// handlers. CommonJS so the handlers can require it (see formSchemas.js).
//
// The field shows and submits "+1 XXX-XXX-XXXX". Keep that exact format: the handlers send
// `+${hardTrim(phone)}` downstream and the client sale form uses the digits as the account password.

const PREFIX = '+1 ';

/**
 * How many leading 0s/1s to drop from an edit of the shown number: only while there are more than
 * 10 digits, so something pasted in front of a full number ("+1 00 262…") is removed but a 0 or 1
 * the visitor left by editing the area code is not.
 */
const extraLeadingDigits = (digits) => {
    let count = 0;
    while (digits.length - count > 10 && (digits[count] === '0' || digits[count] === '1')) count++;
    return count;
};

/**
 * Turns whatever is in the phone field into the 10-digit national number. US area codes never
 * start with 0 or 1, so a leading 0 or 1 is never allowed to become the first area-code digit and
 * push the visitor's last digit off the end (+11 leads, WEBDEV-4574; leading 0, WEBDEV-4617).
 *
 * Text that still starts with the field's own "+1 " is an edit of the shown number, so it is kept as
 * typed (a 0 or 1 left at the front by deleting the first area-code digit is the visitor's, and
 * validation flags it) unless the edit leaves more than 10 digits starting with 0 or 1, which only
 * happens when something was pasted or typed in front of a full number.
 * Anything else (a key typed into the empty field, a paste, autofill) has its leading 0s and 1s
 * dropped: a 1 is the country code, and a 0 can't start a US number.
 */
const normalizePhoneInput = (raw) => {
    const text = raw || '';
    if (text.startsWith(PREFIX)) {
        const digits = text.slice(PREFIX.length).replace(/\D/g, '');
        return digits.slice(extraLeadingDigits(digits)).slice(0, 10);
    }
    return text
        .replace(/\D/g, '')
        .replace(/^[01]+/, '')
        .slice(0, 10);
};

/** "+1 XXX-XXX-XXXX", filled in as far as the digits go; '' for no digits. */
const formatPhone = (digits) => {
    if (!digits) return '';
    let shown = PREFIX + digits.slice(0, 3);
    if (digits.length > 3) shown += '-' + digits.slice(3, 6);
    if (digits.length > 6) shown += '-' + digits.slice(6, 10);
    return shown;
};

/**
 * Where the cursor goes after reformatting: right after the same national digit it followed in
 * `raw`, so an edit in the middle of the number does not throw the cursor to the end.
 */
const phoneCaretPosition = (raw, caret, formatted) => {
    if (!formatted) return 0;
    const text = raw || '';
    let before;
    if (text.startsWith(PREFIX)) {
        const all = text.slice(PREFIX.length).replace(/\D/g, '');
        before = text.slice(PREFIX.length, Math.max(caret, PREFIX.length)).replace(/\D/g, '').length;
        before = Math.max(0, before - extraLeadingDigits(all));
    } else {
        const all = text.replace(/\D/g, '');
        const dropped = (all.match(/^[01]+/) || [''])[0].length;
        before = Math.max(0, text.slice(0, caret).replace(/\D/g, '').length - dropped);
    }
    if (before === 0) return PREFIX.length;
    let seen = 0;
    for (let i = PREFIX.length; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) seen++;
        if (seen === before) {
            // Step past a dash the mask added right after this digit, as the old mask did.
            return formatted[i + 1] === '-' ? i + 2 : i + 1;
        }
    }
    return formatted.length;
};

/** Ten digits after the +1 with an area code that starts with 2-9, ignoring punctuation. */
const isValidUSPhone = (phone) =>
    /^[2-9]\d{9}$/.test(
        String(phone || '')
            .replace(/^\+1/, '')
            .replace(/\D/g, '')
    );

module.exports = { normalizePhoneInput, formatPhone, phoneCaretPosition, isValidUSPhone };
